import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { CustomerReferredFriendRecord, CustomerReferredFriendsRecord } from '@navio/nsdk';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  NoddiBasicCard,
  NoddiTabs,
  TablePagination
} from 'noddi-ui-web';
import { useState } from 'react';
import { useCustomerAppPagination } from '../../hooks/useCustomerAppPagination';
import { getCommonTranslations, getTabsTranslations } from '../../utils/commonTranslations';
import {
  filterReferredFriendsByTab,
  referralFriendStatusSubtext,
  type ReferredFriendsTab
} from '../../utils/referralFriendDisplay';
import { CustomerReferralJourneyTimeline } from './CustomerReferralJourneyTimeline';
import { CustomerReferralStatusBadge } from './CustomerReferralStatusBadge';

enum ReferredFriendsTabs {
  ALL = 'all',
  COMPLETED = 'successful',
  WAITING = 'waiting',
  EXPIRED = 'expired'
}

type Props = {
  referredFriends: CustomerReferredFriendsRecord;
};

type ReferredFriendsListProps = {
  friends: CustomerReferredFriendRecord[];
  pagination: { pageIndex: number; pageSize: number; search?: string };
  onPaginationChange: (params: { pageIndex: number; pageSize: number; search?: string }) => void;
  expandedFriendIds: string[];
  onExpandedFriendIdsChange: (friendIds: string[]) => void;
  emptyMessage: string;
};

const ReferredFriendsList = ({
  friends,
  pagination,
  onPaginationChange,
  expandedFriendIds,
  onExpandedFriendIdsChange,
  emptyMessage
}: ReferredFriendsListProps) => {
  if (friends.length === 0) {
    return <p className='text-sm text-muted-foreground'>{emptyMessage}</p>;
  }

  const pageStart = pagination.pageIndex * pagination.pageSize;
  const pageFriends = friends.slice(pageStart, pageStart + pagination.pageSize);

  return (
    <div className='flex flex-col gap-2'>
      <Accordion
        value={expandedFriendIds}
        onValueChange={(values) => {
          const openedFriendId = values.find((friendId) => !expandedFriendIds.includes(friendId));
          onExpandedFriendIdsChange(openedFriendId != null ? [openedFriendId] : []);
        }}
        className='flex flex-col gap-2'
      >
        {pageFriends.map((friend) => (
          <AccordionItem
            key={friend.referredUserGroupId}
            value={String(friend.referredUserGroupId)}
            className='overflow-hidden rounded-xl border border-b-0 border-border bg-card'
          >
            <AccordionTrigger className='items-center p-4 hover:no-underline'>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <div className='flex min-w-0 flex-1 flex-col gap-0.5 text-left'>
                  <p className='truncate font-semibold'>{friend.displayName}</p>
                  <p className='truncate text-sm font-normal text-muted-foreground'>
                    {referralFriendStatusSubtext(friend.status)}
                  </p>
                </div>
                <CustomerReferralStatusBadge status={friend.status} />
              </div>
            </AccordionTrigger>
            <AccordionContent className='px-4 pb-4'>
              <CustomerReferralJourneyTimeline friend={friend} showHeading={false} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {friends.length > pagination.pageSize && (
        <NoddiBasicCard className='p-4'>
          <TablePagination
            pagination={pagination}
            onChange={onPaginationChange}
            totalCount={friends.length}
            translations={getCommonTranslations().table}
          />
        </NoddiBasicCard>
      )}
    </div>
  );
};

export const ReferredFriendsSection = ({ referredFriends }: Props) => {
  const { pagination, setPagination } = useCustomerAppPagination();
  const [expandedFriendIds, setExpandedFriendIds] = useState<string[]>([]);

  if (referredFriends.referredCount === 0) {
    return null;
  }

  const handlePaginationChange = ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
    setExpandedFriendIds([]);
    setPagination({ pageIndex, pageSize, search: pagination.search });
  };

  const handleTabChange = () => {
    setExpandedFriendIds([]);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize, search: pagination.search });
  };

  const renderTab = (tab: ReferredFriendsTab, emptyMessage: string) => (
    <ReferredFriendsList
      friends={filterReferredFriendsByTab(referredFriends.friends, tab)}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      expandedFriendIds={expandedFriendIds}
      onExpandedFriendIdsChange={setExpandedFriendIds}
      emptyMessage={emptyMessage}
    />
  );

  return (
    <section className='flex flex-col gap-3'>
      <h2 className='text-lg font-semibold text-primary'>
        <Trans>Your invited friends</Trans>
      </h2>

      <NoddiTabs<ReferredFriendsTabs>
        translations={getTabsTranslations()}
        defaultValue={ReferredFriendsTabs.ALL}
        searchParamName='referral-friends-tab'
        onValueChange={handleTabChange}
        tabItems={[
          {
            label: t`All`,
            value: ReferredFriendsTabs.ALL,
            count: referredFriends.referredCount,
            node: renderTab('all', t`You have not referred any friends yet.`)
          },
          {
            label: t`Completed`,
            value: ReferredFriendsTabs.COMPLETED,
            count: referredFriends.successfulCount,
            node: renderTab('successful', t`You have no completed referrals yet.`)
          },
          {
            label: t`Waiting`,
            value: ReferredFriendsTabs.WAITING,
            count: referredFriends.pendingCount,
            node: renderTab('waiting', t`You have no referrals waiting for an order yet.`)
          },
          {
            label: t`Expired`,
            value: ReferredFriendsTabs.EXPIRED,
            count: referredFriends.expiredCount,
            node: renderTab('expired', t`You have no expired referrals.`)
          }
        ]}
      />
    </section>
  );
};
